import { useEffect, useState } from "react";
import { getBandCostSummary, type BandCostSummary } from "../../infra/api.js";

interface Props {
  bandId: string;
  /** Notification threshold — defaults to spec §F8 80%. */
  warnAt?: number;
}

/**
 * Spec §F8: per-band monthly USD badge. Reads `GET /api/bands/:id/cost-summary`
 * and renders a small inline indicator. Goes red/orange at ≥80% of the
 * server-supplied cap so we don't have to know the cap on the client.
 */
export function BandCostBadge({ bandId, warnAt = 80 }: Props): JSX.Element | null {
  const [summary, setSummary] = useState<BandCostSummary | null>(null);

  useEffect(() => {
    const ctl = new AbortController();
    void (async () => {
      try {
        const s = await getBandCostSummary(bandId, ctl.signal);
        if (!ctl.signal.aborted) setSummary(s);
      } catch {
        // Silent — cost is supplementary info.
      }
    })();
    return () => ctl.abort();
  }, [bandId]);

  if (!summary) return null;

  const warning = summary.percent >= warnAt;
  const usd = summary.usd_this_month.toFixed(2);

  return (
    <span
      data-testid={`cost-badge-${bandId}`}
      data-warning={warning ? "true" : "false"}
      title={`USD ${usd} de USD ${summary.cap_usd.toFixed(2)} (${summary.percent.toFixed(1)}%)`}
      className={
        "inline-flex items-center gap-1 border px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest2 " +
        (warning
          ? "border-vinyl bg-vinyl/10 text-vinyl"
          : "border-border bg-tapeElev text-ink-muted")
      }
    >
      {warning && <span aria-hidden>!</span>}
      <span>{`$${usd}`}</span>
      <span className="text-ink-dim">/mês</span>
    </span>
  );
}
